/**
 * Which enrollments a day-of roster is about.
 *
 * An enrollment carries its own term (`enrollment_start_date` … `enrollment_end_date`),
 * which is not the program's: the four after-school programs all run from the first day of
 * the BHCS year, but a family whose school starts later — Kids Connection begins in
 * September — buys a September start against that same program. Listing those children on
 * an August roster puts names on a pickup line nobody is standing in.
 *
 * Shared by the admin pickup board and the teacher roster so the two cannot disagree about
 * who is expected today.
 */

/** The enrollment fields the rule needs. */
export interface TermRow {
  enrollment_start_date: string;
}

export interface RosterEnrollmentSelection<T> {
  enrollments: T[];
  /**
   * True when nothing has started yet, so the roster is showing the term that is about to
   * begin rather than the one being dismissed today.
   */
  isUpcoming: boolean;
  /** First day of the term on screen — the soonest start, when upcoming. */
  termStartDate: string | null;
}

/**
 * Given every enrollment whose term has not ended, the ones the roster should show.
 *
 * Two different questions, depending on where in the year you are:
 *
 * - **Term running** — only enrollments that have already started. A student who joins in
 *   September is not on today's pickup line in August, and counting them would make the
 *   card tallies claim children who are not there.
 * - **Term not started** — all of them. On the day before the first day of school every
 *   enrollment is still in the future, and an empty board is the least useful thing to
 *   show someone preparing that first dismissal.
 *
 * Exported for testing: this is the rule the roster is most likely to get wrong, and it
 * is only wrong on a handful of days a year.
 */
export function selectRosterEnrollments<T extends TermRow>(
  notEnded: T[],
  today: string,
): RosterEnrollmentSelection<T> {
  const started = notEnded.filter(e => e.enrollment_start_date <= today);
  if (started.length > 0) {
    return { enrollments: started, isUpcoming: false, termStartDate: null };
  }
  if (notEnded.length === 0) {
    return { enrollments: [], isUpcoming: false, termStartDate: null };
  }
  return {
    enrollments: notEnded,
    isUpcoming: true,
    termStartDate: notEnded.map(e => e.enrollment_start_date).sort()[0],
  };
}
